import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import type { ApiResponse } from '@shared-models';
import { formatCuitMask, toCuitApiValue } from '@shared-models';
import { API_CONFIG, ApiConfig } from '../../api-config';

/**
 * Payload / response del sub-recurso fiscal de empresa.
 *
 * PUT/GET `/api/structure/companies/{companyId}/fiscal`
 */
export interface CompanyFiscalApi {
  typeOfTaxpayerId?: string;
  taxCategoryId?: string;
  regimenId?: string;
  typeCompanyId?: string;
  sizeCompanyId?: string;
  businessActivityId?: string;
  taxId?: string;
}

/** Shape UI del form de datos fiscales. */
export interface CompanyFiscalFormValue {
  tipoContribuyente: string;
  categoriaFiscal: string;
  regimenFiscal: string;
  tipoEmpresa: string;
  tamanoEmpresa: string;
  actividadEconomica: string;
  cuit: string;
}

/**
 * Sub-recurso fiscal de empresa (solo edit: GET + PUT).
 */
@Injectable({ providedIn: 'root' })
export class CompanyFiscalService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/structure/companies`;
  }

  get(companyId: string): Observable<CompanyFiscalApi | null> {
    return this.httpClient
      .get<ApiResponse<CompanyFiscalApi | null>>(
        `${this.apiUrl}/${companyId}/fiscal`,
      )
      .pipe(map((response) => response.data ?? null));
  }

  update(
    companyId: string,
    payload: CompanyFiscalApi,
  ): Observable<CompanyFiscalApi | null> {
    return this.httpClient
      .put<ApiResponse<CompanyFiscalApi | null>>(
        `${this.apiUrl}/${companyId}/fiscal`,
        payload,
      )
      .pipe(map((response) => response.data ?? null));
  }

  /** API → form UI (taxId sin guiones → máscara en pantalla). */
  toFormValue(data: CompanyFiscalApi | null): CompanyFiscalFormValue {
    return {
      tipoContribuyente: data?.typeOfTaxpayerId ?? '',
      categoriaFiscal: data?.taxCategoryId ?? '',
      regimenFiscal: data?.regimenId ?? '',
      tipoEmpresa: data?.typeCompanyId ?? '',
      tamanoEmpresa: data?.sizeCompanyId ?? '',
      actividadEconomica: data?.businessActivityId ?? '',
      cuit: formatCuitMask(data?.taxId ?? ''),
    };
  }

  /** Form UI → body PUT (CUIT solo dígitos, sin guiones de máscara). */
  toUpsertPayload(form: CompanyFiscalFormValue): CompanyFiscalApi {
    const taxId = toCuitApiValue(form.cuit);
    return {
      typeOfTaxpayerId: form.tipoContribuyente || undefined,
      taxCategoryId: form.categoriaFiscal || undefined,
      regimenId: form.regimenFiscal || undefined,
      typeCompanyId: form.tipoEmpresa || undefined,
      sizeCompanyId: form.tamanoEmpresa || undefined,
      businessActivityId: form.actividadEconomica || undefined,
      taxId: taxId || undefined,
    };
  }

  hasPersistedData(data: CompanyFiscalApi | null): boolean {
    if (!data) return false;
    return !!(
      data.typeOfTaxpayerId ||
      data.taxCategoryId ||
      data.regimenId ||
      data.typeCompanyId ||
      data.sizeCompanyId ||
      data.businessActivityId ||
      data.taxId
    );
  }
}
